import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import Modal from "../../misc/components/modal";
import { Form, Option, Checkbox, Scope, useFormApi } from "informed";
import { Dropdown } from "../../forms/informedv2/Dropdown";
import { TextArea } from "../../forms/informedv2/TextArea";
import { TextBox } from "../../forms/informedv2/TextBox";
import { MultiSelect } from "../../forms/informedv2/MultiSelect";
import { DefinedReportParameterEnum } from "../../misc/api/DefinedReportParameterTypeEnum";
import LoadingWithMessage, { FontSize } from "../../misc/components/LoadingWithMessage";
import { isModelWithError, Query, QueryOrderDirection, Report } from "../../misc/api/apiTypes";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { DefinedReport, DefinedReportParameter, WebPortalClient } from "webportalclient";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";

interface ReportModalProps {
    show?: boolean;
    onCloseClick?: React.MouseEventHandler;
    definedReportId?: string;
}

interface ReportModalFormProps {
    show?: boolean;
    isSaving: boolean;
    setSaving: React.Dispatch<React.SetStateAction<boolean>>;
    onCloseClick: React.MouseEventHandler;
    definedReportId?: string;
}

interface ReportModalFormState {
    path: string;
    name: string;
    description: string;
    roles: any[];
    isWidget: boolean;
    parameters: any;
}

const ReportModalForm = ({ show = true, isSaving, setSaving, onCloseClick, definedReportId }: ReportModalFormProps) => {
    const [isReportSelected, setReportSelected] = useState(false);
    const [reports, setReports] = useState([]);
    const [definedReport, setDefinedReport] = useState<DefinedReport>(null);
    const [report, setReport] = useState(null);
    const [userGroups, setUserGroups] = useState([]);
    const [reportParameters, setReportParameters] = useState([]);
    const [isFetchingReports, setFetchingReports] = useState(false);
    const [isFetchingReportParameters, setFetchingReportParameters] = useState(false);
    const [reportParametersError, setReportParametersError] = useState(false);
    const [loadingValues, setLoadingValues] = useState(false);
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();

    // HACK: Ref required until the informed library is updated to latest version that uses hooks...
    const reportPathRef = useRef();

    const formApi = useFormApi<ReportModalFormState>();

    useEffect(() => {
        const getDefinedReport = async () => {
            const definedRep = await client.client.getDefinedReport(definedReportId);
            setDefinedReport(definedRep);
            setLoadingValues(true);
        }

        if (definedReportId) {
            getDefinedReport();
        }
    }, []);

    useEffect(
        () => {
            if (reports.length > 0) {
                let rep: any = {};
                if (report)
                    rep = reports.find(item => item.Id == report);
                else if (definedReport)
                    rep = reports.find(item => item.Path == definedReport.path);

                if (rep) {
                    formApi.reset();
                    formApi.setValue("path", rep.Id);
                    reportPathRef.current = rep.Path;
                    setReport(rep.Id);
                    setReportParameters([]);
                    setReportSelected(true);
                }
            }
        }, [reports, report, definedReport]);

    useEffect(() => {
        if (report && definedReport) {
            formApi.setValue("name", definedReport.name);
            formApi.setValue("description", definedReport.description);
            formApi.setValue("isWidget", definedReport.isWidget);
        }
    }, [definedReport, report]);

    useEffect(() => {
        if (report && definedReport && userGroups.length > 0) {
            const roles = definedReport.rolesIds.map(r => userGroups.find(g => g.Id == r));
            formApi.setValue("roles", roles);
            setLoadingValues(false);
        }
    }, [definedReport, userGroups, report])

    useEffect(() => {
        if (definedReport && reportParameters.length > 0) {
            for (let p of definedReport.parameters) {
                formApi.setValue(`parameters.${p.name}.type`, p.parameterTypeId);
            }
        }
    }, [definedReport, reportParameters]);

    useEffect(() => {
        let isCanceled = false;

        setFetchingReports(true);

        const initialQuery: Query<Report> = {};
        legacyClient.getReports(initialQuery).then(query => {
            setFetchingReports(false);
            if (!isCanceled) setReports(query.Data ? query.Data : []);
        });

        return () => { isCanceled = true };
    }, []);

    useEffect(() => {
        let isCanceled = false;

        legacyClient.getRoles({
            Order: {
                Member: "Name",
                Direction: QueryOrderDirection.Ascending
            }
        }).then(query => {
            if (!isCanceled) setUserGroups(query.Data ? query.Data : []);
        });

        return () => { isCanceled = true };
    }, []);

    useEffect(
        () => {
            let isCanceled = false;

            if (report) {
                setFetchingReportParameters(true);
                legacyClient.getReportParameters(report).then(data => {
                    setFetchingReportParameters(false);
                    if (isModelWithError(data)) {
                        return setReportParametersError(true);
                    }

                    if (!isCanceled)
                        setReportParameters(data ? data : []);
                });
            }

            return () => {
                isCanceled = true;
                setReportParametersError(false);
            };
        },
        [report]
    );

    useEffect(
        () => {
            if (show) {
                setReportSelected(false);
                setSaving(false);
            }
        },
        [show]
    );

    const reFormatParameters = (parameters): DefinedReportParameter[] => {
        if (!parameters)
            return [];

        var result = [];
        for (var key of Object.keys(parameters)) {
            var parameter: DefinedReportParameter = { name: key, parameterTypeId: parameters[key].type };
            result.push(parameter);
        }
        return result;
    }

    const handleSubmit = useCallback(
        async () => {
            setSaving(true);
            let state = formApi.getState();

            // HACK: Remove empty parameters.
            if (state.values.parameters) {
                for (var propName in state.values.parameters) {
                    if (state.values.parameters[propName].type == "") {
                        delete state.values.parameters[propName];
                    }
                }
            }

            formApi.validate();

            if (!definedReportId)
                await validateUniqueReportName(state.values.name);

            state = formApi.getState();
            if (state.invalid) {
                setSaving(false);
                return;
            }

            const report: DefinedReport = {
                path: reportPathRef.current,
                name: state.values.name,
                description: state.values.description,
                rolesIds: state.values.roles.map(r => r.Id),
                parameters: reFormatParameters(state.values.parameters),
                isWidget: state.values.isWidget
            }
            definedReportId ? await client.client.patchDefinedReport(report, definedReportId) : await client.client.postDefinedReport(report);

            onCloseClick(null);
        },
        [reportPathRef]
    );

    const isWhitespaceString = str => str && !str.toString().replace(/\s/g, '').length;
    const handleTextInputRequiredValidate = value => (!value || isWhitespaceString(value) ? "Field is required." : undefined);

    const handleRequiredValidate = value => (!value ? "Field is required." : undefined);

    const validateUniqueReportName = async name => {
        if (!name)
            return;

        const definedReports = await client.client.getDefinedReports();
        const exists = definedReports.value.find(item => item.name == name);

        if (exists)
            formApi.setError("name", "Report name already exists.");
    };

    return (
        <>
            <div className="modal-body">
                <div className="form-group row">
                    <div className="col-sm-3">
                        <label className="pull-right">Report</label>
                    </div>
                    <div className="col-sm-9">
                        <p>Select a report to setup.</p>
                        {reports && !!reports.length ? (
                            <Dropdown
                                field="path"
                                className="form-control"
                                validate={handleRequiredValidate}
                                value=""
                                disabled={isSaving || loadingValues}
                                onChange={(e) => setReport(e.target.value)}
                            >
                                <Option value="" disabled />
                                {reports.map(item => (
                                    <Option key={item.Id} value={item.Id}>
                                        {item.Path}
                                    </Option>
                                ))}
                            </Dropdown>
                        ) : isFetchingReports ? (
                            <LoadingWithMessage
                                size={FontSize.OneX}
                                message="Loading reports."
                            />
                        ) : (
                            "No reports available."
                        )}
                    </div>
                </div>
                <div className="form-group row">
                    <div className="col-sm-3">
                        <label className="pull-right">Name</label>
                    </div>
                    <div className="col-sm-9">
                        <p>Enter a title to display for the report.</p>
                        <TextBox
                            field="name"
                            className="form-control"
                            validate={handleTextInputRequiredValidate}
                            //asyncValidate={validateUniqueReportName}
                            disabled={!isReportSelected || isSaving || loadingValues}
                        />
                    </div>
                </div>
                <div className="form-group row">
                    <div className="col-sm-3">
                        <label className="pull-right">Description</label>
                    </div>
                    <div className="col-sm-9">
                        <p>Enter a description of the report report.</p>
                        <TextArea
                            field="description"
                            style={{ resize: "none" }}
                            className="form-control"
                            validate={handleTextInputRequiredValidate}
                            disabled={!isReportSelected || isSaving || loadingValues}
                        />
                    </div>
                </div>
                <div className="form-group row">
                    <div className="col-sm-3">
                        <label className="pull-right">User Roles</label>
                    </div>
                    <div className="col-sm-9">
                        <p>
                            Select one or more user roles to allow access to the
                            report.
                        </p>
                        <MultiSelect
                            id="roles"
                            field="roles"
                            validate={handleRequiredValidate}
                            multi
                            valueKey="Id"
                            labelKey="Name"
                            options={userGroups}
                            disabled={!isReportSelected || isSaving || loadingValues}
                        />
                    </div>
                </div>
                <div className="form-group row">
                    <div className="col-sm-3">
                        <label className="pull-right">Widget</label>
                    </div>
                    <div className="col-sm-9">
                        <p>
                            Mark this report as a widget to display on the
                            dashboard.
                        </p>
                        <Checkbox
                            field="isWidget"
                            disabled={!isReportSelected || isSaving || loadingValues}
                        />
                    </div>
                </div>
                {!isFetchingReportParameters &&
                    reportParameters &&
                    reportParameters.length > 0 ? (
                    <div className="form-group row">
                        <div className="col-sm-3">
                            <label className="pull-right">Parameters</label>
                        </div>
                        <div className="col-sm-9">
                            <p>Select how report parameters should be populated.</p>
                        </div>
                    </div>
                ) : isFetchingReportParameters ? (
                    <LoadingWithMessage
                        size={FontSize.OneX}
                        message="Loading report parameters."
                    />
                ) : reportParametersError ? (
                    <Alert show={true} category={AlertCategoryEnum.Danger}>
                        Failed to load report parameters
                    </Alert>
                ) : null}
                {reportParameters.map((item, index) => (
                    <Scope key={item.Name} scope={`parameters.${item.Name}`}>
                        <div className="form-group row">
                            <div className="col-sm-3">
                                <label className="pull-right">
                                    <i>{item.Prompt || item.Name}</i>
                                </label>
                            </div>
                            <div className="col-sm-9">
                                <Dropdown
                                    field="type"
                                    className="form-control"
                                    value=""
                                    disabled={!isReportSelected || isSaving || loadingValues}
                                    data-parameter-name={item.Name}
                                >
                                    <Option value="" />
                                    <Option
                                        value={DefinedReportParameterEnum.UserId}
                                    >
                                        User
                                    </Option>
                                    <Option
                                        value={DefinedReportParameterEnum.StudyId}
                                    >
                                        Study
                                    </Option>
                                </Dropdown>
                            </div>
                        </div>
                    </Scope>
                ))}
            </div>
            <div className="modal-footer">
                <button
                    type="submit"
                    onClick={handleSubmit}
                    className="btn btn-primary"
                    disabled={
                        isSaving || isFetchingReports || isFetchingReportParameters
                    }
                >
                    Save
                </button>
                <button
                    className="btn btn-danger"
                    disabled={isSaving}
                    onClick={onCloseClick}
                >
                    Cancel
                </button>
            </div>
        </>
    );
};

export const ReportModal = ({ show = true, onCloseClick, definedReportId: definedReportId }: ReportModalProps) => {
    const [isSaving, setSaving] = useState(false);

    return (
        <Modal
            show={show}
            header={definedReportId ? "Edit Report" : "Add Report"}
            closeModalHandler={e => (isSaving ? null : onCloseClick(e))}
        >
            <Form<ReportModalFormState>>
                <ReportModalForm definedReportId={definedReportId} show={show} onCloseClick={onCloseClick} isSaving={isSaving} setSaving={setSaving} />
            </Form>
        </Modal>
    );
}

export default ReportModal;
